/**
 * ChargeTimeline - Timeline visual de eventos da cobrança
 * Exibe a jornada completa: criação, sincronização, webhooks e pagamento
 * Segue o padrão visual do ContractTimeline.tsx
 * 
 * Recebe dados via props (fornecidos pela RPC get_cobranca_details)
 * ao invés de fazer query direta nas tabelas.
 * 
 * @author Módulo Financeiro
 * @version 3.0.0
 * RELEASE: 2026-03-15 v1.0.0 - Criação do componente de timeline de cobrança
 * RELEASE: 2026-03-15 v2.0.0 - Refatoração para receber dados via props (RPC centralizada)
 * RELEASE: 2026-03-29 v3.0.0 - Exibição de detalhes de edição (change_diff) na timeline
 * RELEASE: 2026-06-10 v3.1.0 - Resolução UUID→nome para meio_pagamento_id/conta_corrente_id via prop `lookups`
 * RELEASE: 2026-06-21 v3.2.0 - Feature 3: renderiza `card_snapshot` (bandeira, últimos 4 e
 *                              titular mascarado) em eventos PAYMENT_CONFIRMED/RECEIVED/ANTICIPATED.
 *                              Dados vêm da RPC (get_cobranca_details v1.5.7 / get_assinatura_details v1.8.0)
 *                              já sanitizados — nunca PAN, BIN, CVV, validade ou token interno.
 */
import React from 'react';
import {
  FileText, Send, CheckCircle2, XCircle, Clock, AlertCircle,
  RefreshCw, RotateCcw, Loader2, Ban, CreditCard, PlusCircle,
  Pencil, ArrowRight, Pause, Play, Rocket, Settings, Undo2,
  Bell, BellOff,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/currency-utils';

// ── Mapa de labels amigáveis para campos do diff ──
const DIFF_FIELD_LABELS: Record<string, string> = {
  valor: 'Valor',
  valor_total: 'Valor total',
  descricao: 'Descrição',
  observacoes: 'Observações',
  data_vencimento: 'Vencimento',
  data_emissao: 'Data emissão',
  status: 'Status',
  meio_pagamento_id: 'Meio de pagamento',
  conta_corrente_id: 'Conta corrente',
  numero_parcelas: 'Nº parcelas',
  moeda: 'Moeda',
  // Juros/Multa e expiração (campos locais)
  juros: 'Juros/Multa',
  dias_expiracao: 'Prazo expiração (dias)',
  dias_expiracao_pos_vencimento: 'Prazo expiração (dias)',
  // Campos do provider (Asaas)
  value: 'Valor',
  dueDate: 'Vencimento',
  description: 'Descrição',
  billingType: 'Forma de pagamento',
  installmentCount: 'Nº parcelas',
  installmentValue: 'Valor parcela',
  discount: 'Desconto',
  interest: 'Juros',
  fine: 'Multa',
  daysAfterDueDateToRegistrationCancellation: 'Cancelamento pós-vencimento (dias)',
  // v3.3.0 (2026-09-12): liga/desliga da régua de cobrança
  reminders_enabled: 'Notificações da régua',
};

/** Campos que representam valores monetários */
const CURRENCY_FIELDS = ['valor', 'valor_total', 'value', 'installmentValue'];

/** Campos que representam datas */
const DATE_FIELDS = ['data_vencimento', 'data_emissao', 'dueDate'];

/** Campos que representam UUIDs de entidades relacionadas e seu lookup correspondente */
const LOOKUP_FIELD_MAP: Record<string, 'meio_pagamento' | 'conta_corrente'> = {
  meio_pagamento_id: 'meio_pagamento',
  conta_corrente_id: 'conta_corrente',
};

/** Mapas de UUID → nome para resolver entidades relacionadas no diff */
export interface ChargeTimelineLookups {
  meio_pagamento?: Record<string, string>;
  conta_corrente?: Record<string, string>;
}

/** Regex simples para detectar UUID v4 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Formata o valor do diff de forma amigável
 * Detecta automaticamente se é moeda, data, UUID de entidade ou texto
 */
function formatDiffValue(
  field: string,
  value: unknown,
  lookups?: ChargeTimelineLookups,
): string {
  if (value === null || value === undefined) return '—';

  // Booleanos — v3.3.0: reminders_enabled exibe Ativadas/Suspensas
  if (typeof value === 'boolean') {
    if (field === 'reminders_enabled') return value ? 'Ativadas' : 'Suspensas';
    return value ? 'Sim' : 'Não';
  }

  // Valores monetários
  if (CURRENCY_FIELDS.includes(field) && typeof value === 'number') {
    return formatCurrency(value);
  }

  // Datas (formato ISO ou YYYY-MM-DD)
  // IMPORTANTE: strings "YYYY-MM-DD" (DATE puro do Postgres) são interpretadas
  // pelo construtor `new Date()` como UTC 00:00, o que provoca shift de -1 dia
  // ao formatar no fuso do navegador (ex.: Brasil UTC-3). Parse manual como
  // data local resolve isso e garante única fonte de verdade.
  if (DATE_FIELDS.includes(field) && typeof value === 'string') {
    const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const [, y, m, d] = ymd;
      const localDate = new Date(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(localDate.getTime())) {
        return format(localDate, 'dd/MM/yyyy', { locale: ptBR });
      }
    }
    // Fallback: ISO completo com timezone (ex.: 2026-06-24T12:34:56Z)
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return format(date, 'dd/MM/yyyy', { locale: ptBR });
      }
    } catch {
      // Se falhar, retorna como string
    }
  }

  // Resolução de UUID → nome (meio_pagamento_id, conta_corrente_id)
  const lookupKey = LOOKUP_FIELD_MAP[field];
  if (lookupKey && typeof value === 'string' && UUID_RE.test(value)) {
    const name = lookups?.[lookupKey]?.[value];
    if (name) return name;
    // Fallback: 8 primeiros caracteres em maiúsculas (padrão UUID Display do projeto)
    return value.slice(0, 8).toUpperCase();
  }

  // Status — tradução básica
  if (field === 'status' && typeof value === 'string') {
    const statusMap: Record<string, string> = {
      pendente: 'Pendente',
      edicao_andamento: 'Edição em andamento',
      aguardando_emissao: 'Aguardando emissão',
      aguardando_pagamento: 'Aguardando pagamento',
      pago: 'Pago',
      parcial: 'Pago parcialmente',
      vencido: 'Vencido',
      cancelado: 'Cancelado',
      estornado: 'Estornado',
    };
    return statusMap[value] || String(value);
  }

  // ── Objetos estruturados ──
  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;

    // Defensivo: se recebermos por engano o par {old, new}, formata o lado "new".
    if (('old' in obj || 'new' in obj) && !('enabled' in obj) && !('multa_valor' in obj) && !('juros_valor' in obj)) {
      return formatDiffValue(field, obj.new ?? obj.old, lookups);
    }

    /** Formata uma parcela "tipo + valor" do bloco de juros local */
    const fmtJurosPart = (tipo: unknown, val: unknown): string | null => {
      const n = Number(val);
      if (!Number.isFinite(n) || n <= 0) return null;
      if (tipo === 'fixed') return formatCurrency(n);
      // default percent
      return `${n}%`;
    };

    // Formato LOCAL: {enabled, multa_tipo, multa_valor, juros_tipo, juros_valor}
    if (
      field === 'juros' ||
      'enabled' in obj ||
      'multa_valor' in obj ||
      'juros_valor' in obj ||
      'multa_tipo' in obj ||
      'juros_tipo' in obj
    ) {
      if (obj.enabled === false || (!('enabled' in obj) && Object.keys(obj).length === 0)) {
        return 'Desativado';
      }
      const multa = fmtJurosPart(obj.multa_tipo, obj.multa_valor);
      const juros = fmtJurosPart(obj.juros_tipo, obj.juros_valor);
      const parts: string[] = [];
      if (multa) parts.push(`Multa ${multa}`);
      if (juros) parts.push(`Juros ${juros}${obj.juros_tipo === 'fixed' ? '' : '/mês'}`);
      return parts.length > 0 ? parts.join(' • ') : 'Ativo';
    }

    // Formato ASAAS: {value, type} para fine/interest
    if ((field === 'fine' || field === 'interest') && 'value' in obj) {
      const v = Number(obj.value);
      if (!Number.isFinite(v) || v === 0) return '—';
      if (obj.type === 'FIXED') return formatCurrency(v);
      return `${v}%`;
    }

    // Fallback: serializa o objeto em JSON enxuto em vez de "[object Object]"
    try {
      return JSON.stringify(obj);
    } catch {
      return '—';

    }
  }

  return String(value);
}

// Props do componente
interface ChargeTimelineProps {
  /** Eventos de timeline (vindos da RPC get_cobranca_details ou get_assinatura_details) */
  timelineEvents?: TimelineEventInput[];
  /** ID do provider (provider_charge_id) para exibir evento virtual de sincronização */
  providerChargeId?: string | null;
  /** Data de criação (evento virtual inicial) */
  createdAt: string;
  /** Indica se os dados ainda estão carregando */
  isLoading?: boolean;
  /** Parcelas (para mapear provider_payment_id → número da parcela) */
  parcelas?: Array<{ id: string; numero_parcela: number; metadata?: Record<string, unknown> | null }>;
  /**
   * Quando true, suprime os eventos virtuais "charge_created"/"charge_synced"
   * (usado no contexto da assinatura, onde o evento de criação vem da própria
   * tabela financeiro_assinatura_eventos via RPC).
   */
  skipVirtualEvents?: boolean;
  /** Mapas para resolver UUIDs de meio_pagamento_id / conta_corrente_id no diff */
  lookups?: ChargeTimelineLookups;
}

/** Tipo do evento vindo da RPC */
export interface TimelineEventInput {
  id: string;
  event_type: string;
  created_at: string;
  processing_status?: string | null;
  /** Mensagem de erro quando processing_status === 'failed' */
  error_message?: string | null;
  /** ID do pagamento no provider (ex: pay_xxx) — identifica a parcela */
  provider_payment_id?: string | null;
  /** Número da fatura no provider (ex: 13488108) — extraído do raw_payload */
  invoice_number?: string | null;
  /** Diff de campos alterados em eventos de edição */
  change_diff?: Record<string, { old: unknown; new: unknown }> | null;
  // v4.0.0 (RELEASE 2026-06-09): origem do evento e autor
  event_source?: 'provider_webhook' | 'manual' | 'system' | null;
  provider_slug?: string | null;
  actor_full_name?: string | null;
  // v5.0.0 (RELEASE 2026-06-09): contexto de assinatura — identifica o ciclo
  cobranca_id?: string | null;
  ciclo_numero?: number | null;
  // v3.2.0 (RELEASE 2026-06-21): snapshot seguro do cartão usado no pagamento
  card_snapshot?: CardSnapshot | null;
}

/**
 * Snapshot seguro do cartão usado no pagamento. Estrutura definida pelas RPCs
 * get_cobranca_details v1.5.7 e get_assinatura_details v1.8.0. NUNCA inclui
 * PAN completo, BIN, CVV, validade ou UUID do token interno — apenas dados
 * já considerados seguros para exibição pela arquitetura PCI/tokenização.
 */
export interface CardSnapshot {
  brand: string | null;          // ex.: 'VISA', 'MASTERCARD'
  last4: string | null;          // últimos 4 dígitos
  holder_masked: string | null;  // ex.: 'MARIA S.' (mascarado server-side)
  type: string | null;           // ex.: 'CREDIT_CARD'
  source: 'tokenized' | 'provider_webhook' | string;
}


/** Mapeamento de event_type do Asaas para ícone, cor e label */
const EVENT_MAP: Record<string, { icon: typeof FileText; color: string; label: string }> = {
  // Eventos de cobrança criados internamente
  charge_created: { icon: PlusCircle, color: 'text-muted-foreground', label: 'Cobrança criada' },
  charge_synced: { icon: Send, color: 'text-blue-500', label: 'Enviada ao provider' },
  charge_sync_failed: { icon: XCircle, color: 'text-destructive', label: 'Falha ao enviar ao provider' },
  // v1.0.0: Eventos de edição de cobrança (RELEASE: 2026-03-29)
  charge_updated: { icon: Pencil, color: 'text-blue-500', label: 'Cobrança editada' },
  charge_update_failed: { icon: XCircle, color: 'text-destructive', label: 'Falha ao editar cobrança' },
  // v4.0.0: Eventos manuais (RELEASE: 2026-06-09)
  charge_marked_paid_manual: { icon: CheckCircle2, color: 'text-green-600', label: 'Pagamento registrado manualmente' },
  charge_cancelled_manual: { icon: Ban, color: 'text-destructive', label: 'Cobrança cancelada manualmente' },
  // v4.1.0 (2026-08-19): estorno de baixa manual ("cancelar recebimento em dinheiro")
  charge_manual_payment_reverted: { icon: Undo2, color: 'text-amber-600', label: 'Recebimento em dinheiro cancelado' },
  charge_cancel_provider_synced: { icon: CheckCircle2, color: 'text-green-500', label: 'Cancelamento sincronizado no provider' },
  charge_cancel_provider_failed: { icon: XCircle, color: 'text-destructive', label: 'Falha ao cancelar no provider' },
  // Eventos do Asaas via webhook
  PAYMENT_CREATED: { icon: CreditCard, color: 'text-blue-500', label: 'Cobrança criada no Asaas' },
  PAYMENT_AWAITING_RISK_ANALYSIS: { icon: Clock, color: 'text-amber-500', label: 'Aguardando análise de risco' },
  PAYMENT_APPROVED_BY_RISK_ANALYSIS: { icon: CheckCircle2, color: 'text-green-500', label: 'Aprovada na análise de risco' },
  PAYMENT_REPROVED_BY_RISK_ANALYSIS: { icon: XCircle, color: 'text-destructive', label: 'Reprovada na análise de risco' },
  PAYMENT_UPDATED: { icon: RefreshCw, color: 'text-blue-500', label: 'Cobrança atualizada' },
  PAYMENT_CONFIRMED: { icon: CheckCircle2, color: 'text-green-500', label: 'Pagamento confirmado' },
  PAYMENT_RECEIVED: { icon: CheckCircle2, color: 'text-green-500', label: 'Pagamento recebido' },
  PAYMENT_ANTICIPATED: { icon: CheckCircle2, color: 'text-green-500', label: 'Pagamento antecipado' },
  PAYMENT_OVERDUE: { icon: Clock, color: 'text-destructive', label: 'Cobrança vencida' },
  PAYMENT_DELETED: { icon: Ban, color: 'text-destructive', label: 'Cobrança removida' },
  PAYMENT_RESTORED: { icon: RefreshCw, color: 'text-blue-500', label: 'Cobrança restaurada' },
  PAYMENT_REFUNDED: { icon: RotateCcw, color: 'text-amber-500', label: 'Estorno realizado' },
  PAYMENT_REFUND_IN_PROGRESS: { icon: Loader2, color: 'text-amber-500', label: 'Estorno em andamento' },
  PAYMENT_CHARGEBACK_REQUESTED: { icon: AlertCircle, color: 'text-destructive', label: 'Chargeback solicitado' },
  PAYMENT_CHARGEBACK_DISPUTE: { icon: AlertCircle, color: 'text-destructive', label: 'Disputa de chargeback' },
  PAYMENT_AWAITING_CHARGEBACK_REVERSAL: { icon: Clock, color: 'text-amber-500', label: 'Aguardando reversão chargeback' },
  PAYMENT_DUNNING_RECEIVED: { icon: CheckCircle2, color: 'text-green-500', label: 'Recebido via negativação' },
  PAYMENT_DUNNING_REQUESTED: { icon: Send, color: 'text-amber-500', label: 'Negativação solicitada' },
  PAYMENT_BANK_SLIP_VIEWED: { icon: FileText, color: 'text-blue-500', label: 'Boleto visualizado' },
  PAYMENT_CHECKOUT_VIEWED: { icon: FileText, color: 'text-blue-500', label: 'Checkout visualizado' },
  // v5.0.0 (RELEASE: 2026-06-09): Eventos da recorrência (assinatura)
  subscription_created:           { icon: PlusCircle,   color: 'text-muted-foreground', label: 'Recorrência criada' },
  subscription_updated:           { icon: Pencil,       color: 'text-blue-500',          label: 'Recorrência editada' },
  subscription_paused:            { icon: Pause,        color: 'text-amber-500',         label: 'Recorrência pausada' },
  subscription_resumed:           { icon: Play,         color: 'text-green-600',         label: 'Recorrência retomada' },
  subscription_canceled:          { icon: Ban,          color: 'text-destructive',       label: 'Recorrência cancelada' },
  subscription_status_changed:    { icon: Settings,     color: 'text-blue-500',          label: 'Status alterado' },
  subscription_cycle_advanced:    { icon: Rocket,       color: 'text-primary',           label: 'Ciclo antecipado' },
  subscription_cycle_canceled:    { icon: Ban,          color: 'text-destructive',       label: 'Ciclo cancelado' },
  subscription_cycle_generated:   { icon: PlusCircle,   color: 'text-blue-500',          label: 'Novo ciclo gerado' },
  // v3.3.0 (RELEASE: 2026-06-22): Traduções PT-BR p/ eventos antes em inglês
  subscription_anticipated:       { icon: Rocket,       color: 'text-primary',           label: 'Cobrança antecipada pelo usuário' },
  subscription_cycle_charged:     { icon: CreditCard,   color: 'text-blue-500',          label: 'Cobrança enviada ao provider' },
  subscription_card_bound:        { icon: CreditCard,   color: 'text-primary',           label: 'Cartão padrão vinculado à recorrência' },
  itens_atualizados:              { icon: Pencil,       color: 'text-blue-500',          label: 'Itens da recorrência atualizados' },
  pro_rata_applied_on_create:     { icon: Settings,     color: 'text-muted-foreground',  label: 'Pro-rata aplicado na criação' },
  // v3.3.0 (2026-09-12): edição/cancelamento individual de parcela (conta sem integração)
  parcela_updated_manual:         { icon: Pencil,       color: 'text-blue-500',          label: 'Parcela editada manualmente' },
  parcela_cancelled_manual:       { icon: Ban,          color: 'text-destructive',       label: 'Parcela cancelada manualmente' },
  // v3.3.0 (2026-09-12): liga/desliga da régua de cobrança
  reminders_enabled_manual:       { icon: Bell,         color: 'text-green-600',         label: 'Notificações da régua ativadas' },
  reminders_disabled_manual:      { icon: BellOff,      color: 'text-amber-600',         label: 'Notificações da régua suspensas' },
};


/** Label para o status de processamento do webhook */
const PROCESSING_STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  processed: { label: 'Processado', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  failed: { label: 'Falhou', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  skipped: { label: 'Ignorado', className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
};

/** Tipo para evento unificado da timeline (virtual + real) */
interface TimelineEvent {
  id: string;
  event_type: string;
  created_at: string;
  processing_status?: string | null;
  /** Mensagem de erro quando processing_status === 'failed' */
  error_message?: string | null;
  /** ID do pagamento no provider */
  provider_payment_id?: string | null;
  /** Número da fatura no provider */
  invoice_number?: string | null;
  /** Diff de campos alterados */
  change_diff?: Record<string, { old: unknown; new: unknown }> | null;
  is_virtual?: boolean;
  // v4.0.0: origem do evento
  event_source?: 'provider_webhook' | 'manual' | 'system' | null;
  provider_slug?: string | null;
  actor_full_name?: string | null;
  // v5.0.0: contexto de assinatura (badge "Ciclo #N")
  ciclo_numero?: number | null;
  // v3.2.0: snapshot seguro do cartão para eventos de pagamento
  card_snapshot?: CardSnapshot | null;
}


/**
 * Componente de timeline visual para eventos da cobrança/assinatura.
 * Exibe a jornada completa (criação → provider → webhooks → pagamento) ou
 * o histórico da recorrência. Recebe dados via props (RPC centralizada),
 * sem queries internas.
 */
export function ChargeTimeline({
  timelineEvents = [],
  providerChargeId,
  createdAt,
  isLoading,
  parcelas = [],
  skipVirtualEvents = false,
  lookups,
}: ChargeTimelineProps) {
  // Monta lista de eventos unificada (evento virtual de criação + webhooks)
  const events: TimelineEvent[] = React.useMemo(() => {
    const list: TimelineEvent[] = [];

    // Evento virtual: criação (suprimido em contextos onde a RPC já entrega
    // o evento real de criação — ex: timeline da assinatura)
    if (!skipVirtualEvents) {
      list.push({
        id: 'virtual-created',
        event_type: 'charge_created',
        created_at: createdAt,
        is_virtual: true,
      });

      if (providerChargeId) {
        const syncDate = timelineEvents[0]?.created_at || createdAt;
        list.push({
          id: 'virtual-synced',
          event_type: 'charge_synced',
          created_at: syncDate,
          is_virtual: true,
        });
      }
    }

    // Eventos reais (vindos da RPC)
    timelineEvents.forEach(log => {
      list.push({
        id: log.id,
        event_type: log.event_type,
        created_at: log.created_at,
        processing_status: log.processing_status,
        error_message: log.error_message,
        provider_payment_id: log.provider_payment_id,
        invoice_number: log.invoice_number,
        change_diff: log.change_diff,
        event_source: log.event_source,
        provider_slug: log.provider_slug,
        actor_full_name: log.actor_full_name,
        ciclo_numero: log.ciclo_numero ?? null,
        card_snapshot: log.card_snapshot ?? null,
      });
    });


    // Ordena por data
    list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    return list;
  }, [createdAt, providerChargeId, timelineEvents, skipVirtualEvents]);

  // Estado de carregamento
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-6 w-6 rounded-full shrink-0" />
            <div className="space-y-1 flex-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Sem eventos (só deveria ter pelo menos o virtual)
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center py-4 text-muted-foreground">
        <AlertCircle className="h-6 w-6 opacity-40 mb-1" />
        <p className="text-xs">Nenhum evento registrado</p>
      </div>
    );
  }

  return (
    <div className="relative space-y-0">
      {/* Linha vertical da timeline */}
      <div className="absolute left-[11px] top-3 bottom-3 w-px bg-border" />

      {events.map((event, index) => {
        const eventInfo = EVENT_MAP[event.event_type] || {
          icon: AlertCircle,
          color: 'text-muted-foreground',
          label: event.event_type,
        };
        const Icon = eventInfo.icon;
        const isLast = index === events.length - 1;
        const processingInfo = event.processing_status
          ? PROCESSING_STATUS_LABELS[event.processing_status]
          : null;

        // v1.5.0: Construir label enriquecido com número da parcela e fatura
        let displayLabel = eventInfo.label;
        const isPaymentEvent = ['PAYMENT_RECEIVED', 'PAYMENT_CONFIRMED', 'PAYMENT_ANTICIPATED'].includes(event.event_type);
        if (isPaymentEvent && event.provider_payment_id && parcelas.length > 0) {
          // Buscar parcela correspondente pelo provider_payment_id no metadata
          const parcelaMatch = parcelas.find(
            p => (p.metadata as Record<string, unknown>)?.provider_payment_id === event.provider_payment_id
          );
          if (parcelaMatch) {
            displayLabel += ` — Parcela #${parcelaMatch.numero_parcela}`;
          }
          if (event.invoice_number) {
            displayLabel += ` (Fatura ${event.invoice_number})`;
          }
        } else if (isPaymentEvent && event.invoice_number) {
          displayLabel += ` (Fatura ${event.invoice_number})`;
        }

        // v3.0.0: Extrair entradas do change_diff para renderização
        const diffEntries = event.change_diff
          ? Object.entries(event.change_diff).filter(
              ([, v]) => v && (v.old !== undefined || v.new !== undefined)
            )
          : [];

        return (
          <div key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
            {/* Ícone do evento */}
            <div className={cn(
              'relative z-10 flex items-center justify-center h-6 w-6 rounded-full shrink-0',
              isLast ? 'bg-primary/10 ring-2 ring-primary/20' : 'bg-muted'
            )}>
              <Icon className={cn('h-3 w-3', eventInfo.color)} />
            </div>

            {/* Conteúdo do evento */}
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={cn(
                'text-xs font-medium',
                isLast ? 'text-foreground' : 'text-muted-foreground'
              )}>
                {displayLabel}
              </p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[10px] text-muted-foreground">
                  {format(new Date(event.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
                {/* v5.0.0: Badge "Ciclo #N" — eventos vinculados a um ciclo */}
                {event.ciclo_numero != null && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-primary/10 text-primary">
                    Ciclo #{event.ciclo_numero}
                  </span>
                )}
                {/* Badge de status de processamento (para webhooks reais) */}
                {processingInfo && !event.is_virtual && (
                  <span className={cn(
                    'inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium',
                    processingInfo.className
                  )}>
                    {processingInfo.label}
                  </span>
                )}
                {/* v4.0.0: Badge de origem do evento (manual vs provider) */}
                {!event.is_virtual && event.event_source === 'manual' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    Manual{event.actor_full_name ? ` — ${event.actor_full_name}` : ''}
                  </span>
                )}
                {!event.is_virtual && event.event_source === 'provider_webhook' && event.provider_slug && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                    Automático — {event.provider_slug}
                  </span>
                )}
                {!event.is_virtual && event.event_source === 'system' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">
                    Sistema{event.actor_full_name ? ` — ${event.actor_full_name}` : ''}
                  </span>
                )}

              </div>
              {/* Mensagem de erro inline para eventos com falha */}
              {event.error_message && event.processing_status === 'failed' && (
                <p className="text-[10px] text-destructive mt-1 leading-snug break-words">
                  {event.error_message}
                </p>
              )}
              {/* v3.2.0 (RELEASE 2026-06-21): snapshot do cartão usado no pagamento.
                  Renderiza apenas dados seguros (bandeira, últimos 4 e titular
                  já mascarado server-side). Nunca exibe PAN, BIN, CVV ou token. */}
              {event.card_snapshot && (event.card_snapshot.last4 || event.card_snapshot.brand) && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
                  <CreditCard className="h-3 w-3 text-muted-foreground/70 shrink-0" />
                  <span className="font-medium text-foreground">
                    {(event.card_snapshot.brand || 'Cartão').toUpperCase()}
                  </span>
                  {event.card_snapshot.last4 && (
                    <span className="font-mono">•••• {event.card_snapshot.last4}</span>
                  )}
                  {event.card_snapshot.holder_masked && (
                    <>
                      <span className="opacity-50">·</span>
                      <span>{event.card_snapshot.holder_masked}</span>
                    </>
                  )}
                  {event.card_snapshot.source === 'tokenized' && (
                    <span className="ml-0.5 inline-flex items-center px-1 py-0.5 rounded-full text-[8px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                      Tokenizado
                    </span>
                  )}
                </div>
              )}
              {/* v3.0.0: Detalhes de campos alterados (change_diff) */}
              {diffEntries.length > 0 && (
                <div className="mt-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 space-y-1">
                  {diffEntries.map(([field, diff]) => {
                    const label = DIFF_FIELD_LABELS[field] || field;
                    return (
                      <div key={field} className="flex items-center gap-1.5 text-[10px] leading-snug flex-wrap">
                        <span className="font-medium text-muted-foreground">{label}:</span>
                        <span className="line-through text-muted-foreground/60">
                          {formatDiffValue(field, diff.old, lookups)}
                        </span>
                        <ArrowRight className="h-2.5 w-2.5 text-muted-foreground/50 shrink-0" />
                        <span className="text-foreground font-medium">
                          {formatDiffValue(field, diff.new, lookups)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
